import { create } from 'zustand';
import { fetchLocationPages } from '@/data/locations';
import type { Session, SupabaseClient } from '@supabase/supabase-js';
import type {
  AccessScope,
  FeaturePermissionLevel,
  PagePermissionLevel,
  SecurityLevel,
} from '../types/security';
import { runSupabaseQuery, toAppError, type AppError } from '../utils/appErrors';

type Nullable<T> = T | null;

export interface SessionProfile {
  id: string;
  is_archived?: boolean | null;
  email?: string | null;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  headshot_url?: string | null;
  home_location_id?: string | null;
  home_region_id?: string | null;
  mobile_phone?: string | null;
  secondary_email?: string | null;
  sms_opt_in?: boolean | null;
  manager_profile_id?: string | null;
  secondary_manager_profile_id?: string | null;
}

export interface AssignedRegion {
  region_id: string;
  name: string | null;
  is_active: boolean;
  is_manager: boolean;
  grant_all_locations: boolean;
}

export type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated' | 'unavailable';
export type AuthContextStatus = 'idle' | 'loading' | 'ready' | 'error';

interface SessionState {
  session: Session | null;
  securityLevel: SecurityLevel | null;
  accessRoleCodes: string[];
  accessConfigured: boolean;
  isAccessPreview: boolean;
  roles: string[];
  profileId: string | null;
  profile: SessionProfile | null;
  allowedLocationIds: string[];
  assignedRegions: AssignedRegion[];
  booting: boolean;
  sessionReady: boolean;
  authStatus: AuthStatus;
  contextStatus: AuthContextStatus;
  principalEpoch: number;
  authIssue: AppError | null;
  impersonatedRoles?: string[] | null;
  pagePermissions: Record<string, PagePermissionLevel>;
  pageScopes: Record<string, AccessScope>;
  pagePermissionsByRole: Record<string, Record<string, PagePermissionLevel>>;
  featurePermissions: Record<string, FeaturePermissionLevel>;
  featureScopes: Record<string, AccessScope>;
  featurePermissionsByRole: Record<string, Record<string, FeaturePermissionLevel>>;
  setImpersonatedRoles?: (r: string[] | null) => void;
  setSession: (s: Session | null, options?: { authStatus?: AuthStatus }) => void;
  replaceSessionForCurrentPrincipal: (s: Session) => boolean;
  setBooting: (b: boolean) => void;
  setSessionReady: (b: boolean) => void;
  setAuthStatus: (status: AuthStatus) => void;
  setContextStatus: (status: AuthContextStatus) => void;
  setAuthIssue: (issue: AppError | null) => void;
  invalidateContextLoad: () => void;
  loadContext: (sb: SupabaseClient, options?: { fresh?: boolean }) => Promise<void>;
}

// Coordinator boot, auth-state changes, and access-context realtime updates can
// converge on the same context refresh. Coalesce overlapping loads by user and
// principal epoch so a later identity can never inherit an earlier load.
// `fresh` skips coalescing for a caller that just changed access on the server
// (an access preview starting or ending): a load already in flight may have read
// the old access, and the new generation supersedes it.
let inFlightContextLoad: { authUserId: string; principalEpoch: number; promise: Promise<void> } | null = null;
let contextLoadGeneration = 0;

const useSessionStore = create<SessionState>((set, get) => ({
  session: null,
  securityLevel: null,
  accessRoleCodes: [],
  accessConfigured: false,
  isAccessPreview: false,
  roles: [],
  profileId: null,
  profile: null,
  allowedLocationIds: [],
  assignedRegions: [],
  booting: true,
  sessionReady: false,
  authStatus: 'checking',
  contextStatus: 'idle',
  principalEpoch: 0,
  authIssue: null,
  pagePermissions: {},
  pageScopes: {},
  pagePermissionsByRole: {},
  featurePermissions: {},
  featureScopes: {},
  featurePermissionsByRole: {},
  setSession: (s, options) => {
    const current = get();
    if (!s) {
      contextLoadGeneration += 1;
      set({
        session: null,
        securityLevel: null,
        accessRoleCodes: [],
        accessConfigured: false,
        isAccessPreview: false,
        roles: [],
        profileId: null,
        profile: null,
        allowedLocationIds: [],
        assignedRegions: [],
        impersonatedRoles: null,
        pagePermissions: {},
        pageScopes: {},
        pagePermissionsByRole: {},
        featurePermissions: {},
        featureScopes: {},
        featurePermissionsByRole: {},
        sessionReady: false,
        authStatus: options?.authStatus ?? 'unauthenticated',
        contextStatus: 'idle',
        booting: options?.authStatus === 'checking',
        principalEpoch: current.session?.user?.id
          ? current.principalEpoch + 1
          : current.principalEpoch,
        authIssue: null,
      });
      return;
    }
    const sameAuthUser = current.session?.user?.id === s.user?.id;
    set({
      session: s,
      securityLevel: sameAuthUser ? current.securityLevel : null,
      accessRoleCodes: sameAuthUser ? current.accessRoleCodes : [],
      accessConfigured: sameAuthUser ? current.accessConfigured : false,
      isAccessPreview: sameAuthUser ? current.isAccessPreview : false,
      roles: sameAuthUser ? current.roles : [],
      profileId: sameAuthUser ? current.profileId : null,
      profile: sameAuthUser ? current.profile : null,
      allowedLocationIds: sameAuthUser ? current.allowedLocationIds : [],
      assignedRegions: sameAuthUser ? current.assignedRegions : [],
      impersonatedRoles: sameAuthUser ? current.impersonatedRoles : null,
      pagePermissions: sameAuthUser ? current.pagePermissions : {},
      pageScopes: sameAuthUser ? current.pageScopes : {},
      pagePermissionsByRole: sameAuthUser ? current.pagePermissionsByRole : {},
      featurePermissions: sameAuthUser ? current.featurePermissions : {},
      featureScopes: sameAuthUser ? current.featureScopes : {},
      featurePermissionsByRole: sameAuthUser ? current.featurePermissionsByRole : {},
      sessionReady: false,
      authStatus: 'authenticated',
      contextStatus: 'loading',
      booting: false,
      principalEpoch: sameAuthUser ? current.principalEpoch : current.principalEpoch + 1,
      authIssue: null,
    });
  },
  replaceSessionForCurrentPrincipal: (s) => {
    const current = get();
    if (!current.session?.user?.id || current.session.user.id !== s.user?.id) {
      return false;
    }
    // A token refresh is transport maintenance, not a principal transition.
    // Preserve every context and readiness field so protected routes and
    // component-local drafts remain mounted while the token changes.
    set({ session: s, booting: false });
    return true;
  },
  setImpersonatedRoles: (r) => set({ impersonatedRoles: r }),
  setBooting: (b) => set({ booting: b }),
  setSessionReady: (b) => set({ sessionReady: b, contextStatus: b ? 'ready' : get().contextStatus }),
  setAuthStatus: (status) => set({ authStatus: status, booting: status === 'checking' }),
  setContextStatus: (status) => set({ contextStatus: status, sessionReady: status === 'ready' }),
  setAuthIssue: (issue) => set({ authIssue: issue }),
  invalidateContextLoad: () => {
    contextLoadGeneration += 1;
    set({ contextStatus: 'error', sessionReady: false });
  },
  loadContext: async (sb: SupabaseClient, options?: { fresh?: boolean }) => {
    const inFlightAuthUserId = get().session?.user?.id;
    if (!inFlightAuthUserId) return;
    const capturedPrincipalEpoch = get().principalEpoch;
    const preserveMountedContext = get().sessionReady && get().contextStatus === 'ready';
    const alreadyRunning = inFlightContextLoad;
    if (
      !options?.fresh
      && alreadyRunning
      && alreadyRunning.authUserId === inFlightAuthUserId
      && alreadyRunning.principalEpoch === capturedPrincipalEpoch
    ) {
      return alreadyRunning.promise;
    }
    const capturedContextGeneration = ++contextLoadGeneration;
    let settleContextLoad: () => void = () => {};
    let rejectContextLoad: (reason?: unknown) => void = () => {};
    let contextLoadSucceeded = false;
    const contextLoad = new Promise<void>((resolve, reject) => {
      settleContextLoad = resolve;
      rejectContextLoad = reject;
    });
    // The shared promise may have no coalesced consumer. Attach a rejection
    // observer so a context failure cannot become an unhandled rejection.
    void contextLoad.catch(() => undefined);
    inFlightContextLoad = {
      authUserId: inFlightAuthUserId,
      principalEpoch: capturedPrincipalEpoch,
      promise: contextLoad,
    };
    if (!preserveMountedContext) {
      set({
        securityLevel: null,
        accessRoleCodes: [],
        accessConfigured: false,
        isAccessPreview: false,
        roles: [],
        profileId: null,
        profile: null,
        allowedLocationIds: [],
        assignedRegions: [],
        pagePermissions: {},
        pageScopes: {},
        pagePermissionsByRole: {},
        featurePermissions: {},
        featureScopes: {},
        featurePermissionsByRole: {},
        sessionReady: false,
        contextStatus: 'loading',
      });
    }
    try {
      const session = get().session;
      const authUser = session?.user;
      if (!authUser?.id) {
        contextLoadSucceeded = true;
        return;
      }

      const profileRow = await runSupabaseQuery(
        () => sb
          .from('user_profiles')
          .select(
            'id, email, is_archived, full_name, first_name, last_name, headshot_url, home_location_id, home_region_id, mobile_phone, secondary_email, sms_opt_in, manager_profile_id, secondary_manager_profile_id'
          )
          .eq('auth_user_id', authUser.id)
          .maybeSingle(),
        'We could not load your account.',
        'auth_context.profile',
      );

      const profileId = profileRow?.id ?? null;
      let securityLevel: SecurityLevel | null = null;
      let accessRoleCodes: string[] = [];
      let compatibilityRoles: string[] = [];
      let accessConfigured = false;
      let isAccessPreview = false;
      let allowedLocationIds: string[] = [];
      let assignedRegions: AssignedRegion[] = [];
      const pagePermissions: Record<string, PagePermissionLevel> = {};
      const pageScopes: Record<string, AccessScope> = {};
      const featurePermissions: Record<string, FeaturePermissionLevel> = {};
      const featureScopes: Record<string, AccessScope> = {};
      const permissionsByRole: Record<string, Record<string, PagePermissionLevel>> = {};
      const featurePermissionsByRole: Record<string, Record<string, FeaturePermissionLevel>> = {};

      if (profileId) {
        const permissionContext = await runSupabaseQuery(
          () => sb.rpc('current_user_permission_context'),
          'We could not load your role permissions.',
          'auth_context.permission_context',
        ) as Record<string, unknown> | null;
        const rawSecurityLevel = String(permissionContext?.security_level || '').toLowerCase();
        securityLevel = ['guest', 'user', 'manager', 'admin', 'super_admin'].includes(rawSecurityLevel)
          ? rawSecurityLevel as SecurityLevel
          : null;
        accessConfigured = permissionContext?.access_configured === true && securityLevel !== null;
        isAccessPreview = permissionContext?.is_access_preview === true;
        accessRoleCodes = Array.isArray(permissionContext?.access_role_codes)
          ? Array.from(new Set(permissionContext.access_role_codes.map((role) => String(role).toLowerCase()).filter(Boolean)))
          : [];
        compatibilityRoles = Array.isArray(permissionContext?.compatibility_roles)
          ? Array.from(new Set(permissionContext.compatibility_roles.map((role) => String(role).toLowerCase()).filter(Boolean)))
          : [];

        const copyPermissionMap = <T extends string>(
          raw: unknown,
          target: Record<string, T>,
          allowed: readonly string[],
        ) => {
          if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return;
          Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
            const normalized = String(value || 'none');
            if (allowed.includes(normalized)) target[key] = normalized as T;
          });
        };
        copyPermissionMap(permissionContext?.page_permissions, pagePermissions, ['none', 'view', 'edit']);
        copyPermissionMap(permissionContext?.page_scopes, pageScopes, ['self', 'assigned', 'site', 'region', 'managed', 'all']);
        copyPermissionMap(permissionContext?.feature_permissions, featurePermissions, ['none', 'view', 'edit_own', 'edit_all']);
        copyPermissionMap(permissionContext?.feature_scopes, featureScopes, ['self', 'assigned', 'site', 'region', 'managed', 'all']);

        const isInternalEmployee = securityLevel !== null && ['user', 'manager', 'admin', 'super_admin'].includes(securityLevel);
        if (isInternalEmployee) {
          const locRows = await runSupabaseQuery(
            () => sb
              .from('user_profile_locations')
              .select('location_id')
              .eq('user_profile_id', profileId),
            'We could not load your assigned locations.',
            'auth_context.locations',
          );
          allowedLocationIds = (locRows || [])
            .map((row: any) => row.location_id)
            .filter((id: Nullable<string>): id is string => Boolean(id));

          const regionRows = await runSupabaseQuery(
            () => sb
              .from('user_profile_regions')
              .select('region_id,is_manager,grant_all_locations, location_regions(region_id,name,is_active)')
              .eq('user_profile_id', profileId)
              .order('assigned_at', { ascending: true }),
            'We could not load your assigned regions.',
            'auth_context.regions',
          );
          assignedRegions = (regionRows || []).map((row: any) => ({
            region_id: row.region_id,
            name: row.location_regions?.name ?? null,
            is_active: row.location_regions?.is_active !== false,
            is_manager: !!row.is_manager,
            grant_all_locations: !!row.grant_all_locations,
          }));

          const grantRegionIds = assignedRegions
            .filter((region) => region.is_active && (region.grant_all_locations || region.is_manager))
            .map((region) => region.region_id);
          if (grantRegionIds.length > 0) {
            const regionLocationRows = await runSupabaseQuery(
              () => fetchLocationPages({ supabase: sb, regionIds: grantRegionIds, activeOnly: false }),
              'We could not load your region locations.',
              'auth_context.region_locations',
            );
            allowedLocationIds = Array.from(new Set([
              ...allowedLocationIds,
              ...(regionLocationRows || [])
                .map((row: any) => row.location_id)
                .filter((id: Nullable<string>): id is string => Boolean(id)),
            ]));
          }

          if (securityLevel === 'admin' || securityLevel === 'super_admin') {
            const allLocationRows = await runSupabaseQuery(
              () => fetchLocationPages({ supabase: sb, activeOnly: false }),
              'We could not load available locations.',
              'auth_context.all_locations',
            );
            allowedLocationIds = (allLocationRows || [])
              .map((row: any) => row.location_id)
              .filter((id: Nullable<string>): id is string => Boolean(id));
          }
        }
      }

      const imp = get().impersonatedRoles;
      const current = get();
      if (
        current.principalEpoch !== capturedPrincipalEpoch
        || current.session?.user?.id !== inFlightAuthUserId
        || contextLoadGeneration !== capturedContextGeneration
      ) {
        contextLoadSucceeded = true;
        return;
      }

      set({
        securityLevel,
        accessRoleCodes,
        accessConfigured,
        isAccessPreview,
        roles: compatibilityRoles,
        profileId,
        profile: profileRow
          ? {
              id: profileRow.id,
              is_archived: profileRow.is_archived ?? false,
              email: profileRow.email ?? authUser.email ?? null,
              full_name: profileRow.full_name ?? null,
              first_name: profileRow.first_name ?? null,
              last_name: profileRow.last_name ?? null,
              home_location_id: profileRow.home_location_id ?? null,
              home_region_id: profileRow.home_region_id ?? null,
              mobile_phone: profileRow.mobile_phone ?? null,
              secondary_email: profileRow.secondary_email ?? null,
              sms_opt_in: profileRow.sms_opt_in ?? false,
              manager_profile_id: profileRow.manager_profile_id ?? null,
              secondary_manager_profile_id: profileRow.secondary_manager_profile_id ?? null,
            }
          : null,
        allowedLocationIds,
        assignedRegions,
        impersonatedRoles: imp,
        pagePermissionsByRole: permissionsByRole,
        pagePermissions,
        pageScopes,
        featurePermissionsByRole,
        featurePermissions,
        featureScopes,
        sessionReady: true,
        contextStatus: 'ready',
        authIssue: null,
      });
      contextLoadSucceeded = true;
    } catch (error) {
      const current = get();
      const stillCurrentPrincipal = (
        current.principalEpoch === capturedPrincipalEpoch
        && current.session?.user?.id === inFlightAuthUserId
        && contextLoadGeneration === capturedContextGeneration
      );
      if (!stillCurrentPrincipal) {
        // A prior principal's late failure is irrelevant to the new session.
        // Do not classify it (classification can emit a session signal) and do
        // not write it into the new principal's authorization state.
        contextLoadSucceeded = true;
        return;
      }
      const appError = toAppError(error, 'We could not load your access settings.', 'auth_context');
      if (preserveMountedContext) {
        set({ authIssue: appError, sessionReady: true, contextStatus: 'ready' });
      } else {
        set({
          securityLevel: null,
          accessRoleCodes: [],
          accessConfigured: false,
          isAccessPreview: false,
          roles: [],
          profileId: null,
          profile: null,
          allowedLocationIds: [],
          assignedRegions: [],
          impersonatedRoles: null,
          pagePermissions: {},
          pageScopes: {},
          pagePermissionsByRole: {},
          featurePermissions: {},
          featureScopes: {},
          featurePermissionsByRole: {},
          sessionReady: false,
          contextStatus: 'error',
          authIssue: appError,
        });
      }
      rejectContextLoad(appError);
      throw appError;
    } finally {
      if (inFlightContextLoad?.promise === contextLoad) inFlightContextLoad = null;
      if (contextLoadSucceeded) settleContextLoad();
    }
  },
}));

export default useSessionStore;
